import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { useForm } from 'react-hook-form';
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import { useUpdateBrief } from '@/entities/brief';
import { notificationService } from '@/lib/services/notification.service';
import { buildEditBriefFormSchema, type EditBriefFormData } from './edit-brief.schema';
import { useTranslation } from 'react-i18next';

export interface EditBriefModalProps {
  open: boolean;
  projectId: string;
  briefId: string;
  /** Current brief title, used to seed the form when the modal opens. */
  currentTitle: string;
  onClose: () => void;
  /** Fired after a successful rename (cache already rebased by `useUpdateBrief`). */
  onSuccess?: (updated: BriefResponseDto) => void;
}

/**
 * Modal for editing a brief. Currently renames only (single `title` field);
 * structured as `edit-brief` so additional editable fields can be added later
 * without renaming the slice. Mirrors `CreateFolderModal` (Dialog +
 * react-hook-form + zod) minus the tags field.
 *
 * `useUpdateBrief` is a pure cache adapter — this modal owns its toasts: a
 * success toast + close on success, an error toast that keeps the dialog open
 * so the user can retry.
 */
export function EditBriefModal(props: EditBriefModalProps) {
  const { t } = useTranslation('briefs');
  const { t: tValidation } = useTranslation('validation');

  const { open, projectId, briefId, currentTitle, onClose, onSuccess } = props;

  const formSchema = useMemo(() => buildEditBriefFormSchema(tValidation), [tValidation]);

  const form = useForm<EditBriefFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: currentTitle },
    // Submit-time rather than `onTouched`: the dialog autofocuses this field,
    // so a blur-time check fires the moment a user who emptied it reaches for
    // the close button, and the message it renders grows the centred dialog
    // enough to slide that 16px button out from under the pointer — swallowing
    // the very click that asked to close. The CTA already stays disabled while
    // the title is empty, so the blur check told the user nothing new.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Reseed the form to the freshest title each time the modal re-opens.
  useEffect(() => {
    if (open) {
      form.reset({ title: currentTitle });
    }
  }, [currentTitle, form, open]);

  const mutation = useUpdateBrief({
    projectId,
    briefId,
    onSuccess: (updated) => {
      notificationService.showSuccess(t('editBrief.editBriefModal.briefRenamed'));
      onSuccess?.(updated);
      onClose();
    },
    onError: (error) => {
      notificationService.showError(t('editBrief.editBriefModal.failedToRenameBrief'), {
        description: error.message,
      });
    },
  });

  const isPending = mutation.isPending;
  const watchedTitle = form.watch('title');
  const canSubmit = !isPending && watchedTitle.trim().length > 0;

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const onSubmit = form.handleSubmit((formData) => {
    mutation.mutate({ body: { title: formData.title.trim() } });
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('editBrief.editBriefModal.renameBrief')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col"
          >
            <DialogBody className="flex flex-col gap-4 py-10">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('editBrief.editBriefModal.briefName')}
                      <span aria-hidden="true" className="text-destructive">
                        *
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('editBrief.editBriefModal.enterTheBriefName')}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                {t('editBrief.editBriefModal.cancel')}
              </Button>
              <Button type="submit" variant="default" disabled={!canSubmit}>
                {isPending && <Spinner />} {t('editBrief.editBriefModal.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
